// SSO landing — entry point for the Loyalty App deeplink:
//   /sso?token=<single-use JWT>&lang=EN|HI|TA
// Exchanges the token for a real session, applies the language, then opens the Feed.
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { setToken } from '@/lib/api';
import { BrandLogo } from '@/lib/brand';
import { Loader2, AlertTriangle } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUPPORTED = ['en', 'hi', 'ta', 'te', 'gu', 'bn', 'kn', 'ml', 'mr', 'or'];
const LOCALE_KEY = 'lms.locale';
const COURSE_LANG_KEY = 'lms.course_lang';

// Module-level guard: the SSO token is single-use, so we must exchange it exactly once.
// A useRef guard is reset when React 18 StrictMode remounts the component in dev, which
// would fire a second (failing) exchange — a module-scoped flag survives the remount.
let ssoExchangeStarted = false;

export default function SsoLanding() {
    const [params] = useSearchParams();
    const [error, setError] = useState('');

    useEffect(() => {
        if (ssoExchangeStarted) return;   // token is single-use — never exchange twice
        ssoExchangeStarted = true;

        const token = params.get('token');
        const langRaw = (params.get('lang') || 'en').toLowerCase();
        const fallbackLang = SUPPORTED.includes(langRaw) ? langRaw : 'en';
        // Loyalty bearer token (used later to credit earned points back to the Loyalty app).
        const zeaplKey = params.get('zeapl_key') || params.get('Zeapl_key') || '';
        // Optional post-login destination (e.g. a specific course). Only allow internal
        // /learn paths to prevent open-redirect; anything else falls back to the Feed.
        const redirectRaw = params.get('redirect') || '/learn';
        const redirectTo = /^\/learn(\/|$|\?)/.test(redirectRaw) ? redirectRaw : '/learn';

        if (!token) { setError('Missing sign-in token. Please reopen from the Propulse app.'); return; }

        (async () => {
            try {
                const { data } = await axios.post(`${API_BASE}/auth/sso/exchange`, { token, zeapl_key: zeaplKey });
                setToken(data.token);
                const lang = (data.lang || fallbackLang).toLowerCase();
                const finalLang = SUPPORTED.includes(lang) ? lang : 'en';
                localStorage.setItem(LOCALE_KEY, finalLang);
                // Doc Point 23: the language passed on the deeplink becomes the default in the
                // trainee language dropdown AND filters which language's courses are shown.
                localStorage.setItem(COURSE_LANG_KEY, finalLang);
                // Doc Point 63: land on the English page URL carrying the language as a
                // `?lang=<code>` query param (e.g. /learn?lang=hi) so the Devnagri widget and
                // the in-app language dropdown both pick it up. Default is `en`.
                const dest = redirectTo + (redirectTo.includes('?') ? '&' : '?') + `lang=${finalLang}`;
                // Hard redirect so AuthProvider re-initialises from the stored session.
                window.location.replace(dest);
            } catch (e) {
                ssoExchangeStarted = false;   // allow a retry (e.g. on remount) if it genuinely failed
                setError(e?.response?.data?.detail || 'This sign-in link is invalid or has expired. Please reopen from the Propulse app.');
            }
        })();
    }, [params]);

    return (
        <div className="min-h-screen bg-slate-50 grid place-items-center px-6" data-testid="sso-landing">
            <div className="w-full max-w-sm text-center">
                <div className="flex justify-center mb-8"><BrandLogo /></div>
                {error ? (
                    <div className="bg-white rounded-2xl border border-brand-navy/5 p-6 shadow-sm" data-testid="sso-error">
                        <AlertTriangle className="w-9 h-9 text-amber-500 mx-auto mb-3" />
                        <div className="font-bold text-brand-black">Couldn&apos;t sign you in</div>
                        <p className="text-sm text-brand-navy/60 mt-1.5">{error}</p>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-3 text-brand-navy/60" data-testid="sso-loading">
                        <Loader2 className="w-8 h-8 animate-spin text-brand-yellow" />
                        <div className="text-sm font-semibold">Signing you in…</div>
                    </div>
                )}
            </div>
        </div>
    );
}
